import React from 'react';
import { Paper, Box, Typography, Chip } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IRadius, INumericSx, ILineHeight } from '../insightsTheme';

/**
 * LeversCard — top N priority levers (derived from problem summary).
 *
 * Props:
 *   levers: [{ n, d, impactLabel, pct, onClick }]
 *   title: optional (defaults to t())
 */
const LeversCard = ({ levers = [], title }) => {
  const { t } = useTranslation();
  const headerTitle = title || t('insights_v2_levers_title');

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${IC.bdr}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: `${IFontSizes.md}px`, fontWeight: IFontWeights.strong, color: IC.tx }}>
            {headerTitle}
          </Typography>
          <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM }}>
            {t('insights_v2_levers_subtitle')}
          </Typography>
        </Box>
        {/* Non-interactive status badge. clickable={false} keeps it from looking like a button;
            the no-op onClick is required because this MUI Chip version's root handler calls
            onClick(event) unconditionally, so a missing/undefined onClick throws on click. */}
        <Chip
          size="small"
          clickable={false}
          icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 12 }} />}
          label={t('insights_v2_levers_ai_badge')}
          onClick={() => {}}
          sx={{
            height: 24,
            fontSize: `${IFontSizes.xs}px`,
            fontWeight: IFontWeights.label,
            color: IC.ai,
            bgcolor: IC.aiSoft,
            border: `1px solid ${IC.aiSoft}`,
            '& .MuiChip-icon': { color: IC.ai, ml: 0.5 },
            '& .MuiChip-label': { pr: 1 },
          }}
        />
      </Box>

      {levers.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: `${IFontSizes.sm}px`, color: IC.txM }}>
            {t('insights_v2_levers_empty')}
          </Typography>
        </Box>
      ) : (
        levers.map((p, i) => (
          <Box
            key={i}
            onClick={p.onClick}
            tabIndex={p.onClick ? 0 : -1}
            onKeyDown={(e) => {
              if (p.onClick && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                p.onClick();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: i < levers.length - 1 ? `1px solid ${IC.bdrL}` : 'none',
              cursor: p.onClick ? 'pointer' : 'default',
              transition: 'background 0.18s ease, transform 0.18s ease',
              '&:hover': p.onClick
                ? { background: IC.surfaceAlt, transform: 'translateX(2px)' }
                : undefined,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                background: IC.pri,
                color: IC.onDark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: `${IFontSizes.md}px`,
                fontWeight: IFontWeights.strong,
                flexShrink: 0,
                ...INumericSx,
              }}
            >
              {i + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: `${IFontSizes.md}px`,
                  fontWeight: IFontWeights.label,
                  color: IC.tx,
                  lineHeight: ILineHeight.feed,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={p.n}
              >
                {p.n}
              </Typography>
              <Typography
                sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.tx2, lineHeight: ILineHeight.feed }}
              >
                {p.d}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: `${IFontSizes.lg}px`,
                  fontWeight: IFontWeights.strong,
                  color: IC.ai,
                  ...INumericSx,
                  lineHeight: 1.1,
                }}
              >
                {p.impactLabel}
              </Typography>
              <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM, ...INumericSx }}>
                {p.impactSuffix ? `${p.impactSuffix} · ` : ''}
                {p.pct != null ? `${p.pct}% ${t('insights_v2_levers_per_month')}` : ''}
              </Typography>
            </Box>
            {p.onClick && (
              <KeyboardArrowRightIcon sx={{ fontSize: 18, color: IC.txM, flexShrink: 0 }} />
            )}
          </Box>
        ))
      )}
    </Paper>
  );
};

export default LeversCard;
